/**
 * Whole-planet region-cell scan ordering (debug rig, W2b-2.7).
 *
 * A PURE function of the planet radius and a reference unit direction: it lists
 * EVERY region cell on the planet — all 6 cube faces × cellsPerFace² cells —
 * ordered nearest-first by the chord distance of each cell's centre direction
 * from the reference, ascending, with a deterministic packCell tie-break. No
 * sampler, no THREE, no worldgen sampling: the seed test is applied by the
 * caller per cell in this order. Extracted so the ordering (coverage +
 * monotone distance + determinism) is oracle-testable in node.
 */
import {
  cellsPerFace,
  cellCenterDir,
  packCell,
  LEVEL_REGION,
  type CellId,
} from "@hyperlight/shared";

/**
 * All region cells of a planet of the given radius, ordered by ascending chord
 * distance of the cell centre from the (fx,fy,fz) reference direction. The
 * reference need not be unit length — it is normalised internally; a zero
 * vector degrades to +x. Tie-break: ascending packCell (a total order over
 * cells), so the result is fully deterministic.
 */
export function planScan(radius: number, fx: number, fy: number, fz: number): CellId[] {
  const n = cellsPerFace(radius, LEVEL_REGION);
  const inv = 1 / (Math.hypot(fx, fy, fz) || 1);
  const rx = fx * inv, ry = fy * inv, rz = fz * inv;
  const c: number[] = [0, 0, 0];
  const scored: { cell: CellId; d2: number; key: number }[] = [];
  for (let face = 0; face < 6; face++) {
    for (let x = 0; x < n; x++) {
      for (let y = 0; y < n; y++) {
        const cell: CellId = { face, x, y, level: LEVEL_REGION };
        cellCenterDir(radius, cell, c);
        const dx = c[0] - rx, dy = c[1] - ry, dz = c[2] - rz;
        // Squared chord distance — monotone in the true chord, so it orders
        // identically while avoiding a sqrt per cell.
        scored.push({ cell, d2: dx * dx + dy * dy + dz * dz, key: packCell(cell) });
      }
    }
  }
  scored.sort((a, b) => a.d2 - b.d2 || a.key - b.key);
  return scored.map((s) => s.cell);
}
